import _extends from "@babel/runtime/helpers/esm/extends";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import PricingConstants from '@sprinx/pricing-core/constants';
import useEnsureIdentHandler from '@sprinx/react-entity-hooks/useEnsureIdentHandler';
import useCreateSubjectIdHandler from '@sprinx/react-pricing/useCreateSubjectIdHandler';
import PropTypes from 'prop-types';
import React from 'react';
import ShoppingCartContext from './ShoppingCartContext';
import useUpdateCart from './useUpdateCart';

function ShoppingCartProvider(_ref) {
  var AddToCartComponent = _ref.AddToCartComponent,
      children = _ref.children,
      _ref$preventDecrement = _ref.preventDecrementRemoveSubject,
      preventDecrementRemoveSubject = _ref$preventDecrement === void 0 ? false : _ref$preventDecrement,
      ProductDetailLinkComponent = _ref.ProductDetailLinkComponent,
      ProductImageComponent = _ref.ProductImageComponent,
      ProductPriceComponent = _ref.ProductPriceComponent,
      productsServiceCallListHook = _ref.productsServiceCallListHook,
      showSnackbarMessage = _ref.showSnackbarMessage,
      TaxesFieldPreviewComponent = _ref.TaxesFieldPreviewComponent,
      UpSalesProductsComponent = _ref.UpSalesProductsComponent;
  var ensureId = useEnsureIdentHandler('entity');
  var createSubjectId = useCreateSubjectIdHandler();

  var _useUpdateCart = useUpdateCart(showSnackbarMessage),
      _useUpdateCart2 = _slicedToArray(_useUpdateCart, 4),
      cartInput = _useUpdateCart2[0],
      flatCart = _useUpdateCart2[1],
      updateCart = _useUpdateCart2[2],
      resetCart = _useUpdateCart2[3],
      manipulateCartDirect = _useUpdateCart2[4];

  var setSubject = React.useCallback(function (subjectData) {
    return updateCart((Array.isArray(subjectData) ? subjectData : [subjectData]).map(function (_ref2) {
      var product = _ref2.product,
          quantity = _ref2.quantity,
          ruleCode = _ref2.ruleCode,
          pSubjectId = _ref2.subjectId;
      return {
        subjectId: pSubjectId || createSubjectId(ensureId(product, ruleCode)),
        product: product,
        quantity: quantity
      };
    }));
  }, [createSubjectId, ensureId, updateCart]);
  var setPayment = React.useCallback(function (cartPaymentProduct) {
    var cartPayment = cartPaymentProduct ? {
      product: cartPaymentProduct
    } : {
      product: PricingConstants.PAYMENT_DEFAULT_PRODUCT
    };
    return setSubject(_extends({}, cartPayment, {
      quantity: 1,
      subjectId: PricingConstants.PAYMENT_ID
    }));
  }, [setSubject]);
  var setShipping = React.useCallback(function (cartShippingProduct) {
    var cartShipping = cartShippingProduct ? {
      product: cartShippingProduct
    } : {
      product: PricingConstants.SHIPPING_DEFAULT_PRODUCT
    };
    return setSubject(_extends({}, cartShipping, {
      quantity: 1,
      subjectId: PricingConstants.SHIPPING_ID
    }));
  }, [setSubject]);
  var presentInCart = React.useCallback(function (_ref3) {
    var product = _ref3.product,
        ruleCode = _ref3.ruleCode,
        pSubjectId = _ref3.subjectId;
    var subjectId = pSubjectId || createSubjectId(ensureId(product, ruleCode));
    return [(flatCart[subjectId] || {}).quantity, subjectId];
  }, [createSubjectId, ensureId, flatCart]);
  var enableIndirectSubject = React.useCallback(function (indirectSubjectId) {
    if (flatCart[indirectSubjectId]) {
      updateCart({
        subjectId: indirectSubjectId,
        enableIndirect: true
      });
    }
  }, [flatCart, updateCart]);
  return /*#__PURE__*/React.createElement(ShoppingCartContext.Provider, {
    value: {
      cart: cartInput,
      enableIndirectSubject: enableIndirectSubject,
      setSubject: setSubject,
      setPayment: setPayment,
      setShipping: setShipping,
      presentInCart: presentInCart,
      preventDecrementRemoveSubject: preventDecrementRemoveSubject,
      resetCart: resetCart,
      AddToCartComponent: AddToCartComponent,
      ProductImageComponent: ProductImageComponent,
      ProductDetailLinkComponent: ProductDetailLinkComponent,
      ProductPriceComponent: ProductPriceComponent,
      TaxesFieldPreviewComponent: TaxesFieldPreviewComponent,
      UpSalesProductsComponent: UpSalesProductsComponent,
      productsServiceCallListHook: productsServiceCallListHook,
      manipulateCartDirect: manipulateCartDirect,
    }
  }, children);
}

process.env.NODE_ENV !== "production" ? ShoppingCartProvider.propTypes = {
  AddToCartComponent: PropTypes.elementType.isRequired,
  children: PropTypes.node,
  preventDecrementRemoveSubject: PropTypes.bool,
  ProductDetailLinkComponent: PropTypes.elementType.isRequired,
  ProductImageComponent: PropTypes.elementType.isRequired,
  ProductPriceComponent: PropTypes.elementType.isRequired,
  productsServiceCallListHook: PropTypes.func.isRequired,
  showSnackbarMessage: PropTypes.func,
  TaxesFieldPreviewComponent: PropTypes.elementType,
  UpSalesProductsComponent: PropTypes.elementType
} : void 0;
ShoppingCartProvider.defaultProps = {
  children: null,
  preventDecrementRemoveSubject: false,
  showSnackbarMessage: undefined,
  TaxesFieldPreviewComponent: undefined,
  UpSalesProductsComponent: undefined
};
export default ShoppingCartProvider;